/**
 * 路由表（T01）。
 *
 * 4 个空页面：会话列表 / 对话 / 角色卡 / 设置；页面组件放 `src/features/<name>/`。
 * `id` 同时用于底部导航按钮的组件清单 id（`app.nav.<id>`），保持稳定可追踪。
 */
import type { ComponentType } from 'react';

import { MessagesPage } from '../features/messages/MessagesPage';
import { MomentsPage } from '../features/moments/MomentsPage';
import { PresetsPage } from '../features/presets/PresetsPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import type { AppIconName } from '../ui';

/** 允许的路由路径（hash 去掉 `#` 后的部分）。 */
export type RoutePath = '/messages' | '/contacts' | '/moments' | '/settings';

export interface RouteDefinition {
  path: RoutePath;
  /** 稳定标识，用于清单 id 与测试断言。 */
  id: 'messages' | 'contacts' | 'moments' | 'settings';
  /** 底部导航显示名。 */
  label: string;
  /** 页面标题。 */
  title: string;
  /** 导航图标（占位字符，后续任务可换成 SVG）。 */
  icon: AppIconName;
  Component: ComponentType;
}

const messagesRoute: RouteDefinition = {
  path: '/messages',
  id: 'messages',
  label: '消息',
  title: '消息',
  icon: 'messages',
  Component: MessagesPage,
};

const contactsRoute: RouteDefinition = {
  path: '/contacts',
  id: 'contacts',
  label: '联系人',
  title: '联系人',
  icon: 'contacts',
  Component: PresetsPage,
};

const momentsRoute: RouteDefinition = {
  path: '/moments',
  id: 'moments',
  label: '朋友圈',
  title: '朋友圈',
  icon: 'moments',
  Component: MomentsPage,
};

const settingsRoute: RouteDefinition = {
  path: '/settings',
  id: 'settings',
  label: '设置',
  title: '设置',
  icon: 'settings',
  Component: SettingsPage,
};

/** 默认路由：空 hash / 未知 hash 都落到这里。 */
export const DEFAULT_ROUTE: RoutePath = messagesRoute.path;

export const ROUTES: readonly RouteDefinition[] = [
  messagesRoute,
  contactsRoute,
  momentsRoute,
  settingsRoute,
];

export function isRoutePath(value: string): value is RoutePath {
  return ROUTES.some((route) => route.path === value);
}

/** 取路由定义；未知路径回落到默认路由。 */
export function matchRoute(path: string): RouteDefinition {
  return ROUTES.find((route) => route.path === path) ?? messagesRoute;
}

/** Hidden chat route; it renders outside the four bottom tabs. */
export const CHAT_ROUTE = '/chat' as const;
